import * as THREE from 'three';

export const HIT_POINTS_NUM = 8;

// Inline shaders based on the demo
const vertexShader = `
varying vec2 vUv;
varying vec3 vWorldPosition;
varying vec3 vNormal;

#include <clipping_planes_pars_vertex>

void main() {
  #include <begin_vertex>
  #include <project_vertex>
  #include <clipping_planes_vertex>
  vUv = uv;
  vWorldPosition = (modelMatrix * vec4(transformed, 1.0)).xyz;
  vNormal = normal;
}`;

const fragmentShader = `
struct HitPoint {
  vec3 position;
  float thickness;
  float size;
};

varying vec2 vUv;
varying vec3 vWorldPosition;
varying vec3 vNormal;

uniform sampler2D t_Noise;

uniform HitPoint u_HitPoints[HIT_POINTS_NUM];
uniform vec4 u_HitPointColorA;
uniform vec4 u_HitPointColorB;

uniform float u_FresnelFalloff;
uniform float u_FresnelStrength;
uniform vec3 u_FresnelColor;

uniform float u_Time;

#include <clipping_planes_pars_fragment>

void main() {
  #include <clipping_planes_fragment>

  vec4 noise = texture2D(t_Noise, vUv*3.0 + u_Time*vec2(0.127, 0.178));

  vec3 normal = normalize(vNormal);
  vec3 viewDirection = normalize(cameraPosition - vWorldPosition);

  float hits = 0.0;
  for (int i = 0; i < HIT_POINTS_NUM; i++) {
    float dOuter = distance(u_HitPoints[i].position, vWorldPosition);
    dOuter = smoothstep(u_HitPoints[i].size, u_HitPoints[i].size+u_HitPoints[i].thickness, dOuter);
    dOuter = 1.0 - dOuter;

    float dInner = distance(u_HitPoints[i].position, vWorldPosition);
    dInner = smoothstep(u_HitPoints[i].size-u_HitPoints[i].thickness, u_HitPoints[i].size, dInner);

    float d = dOuter*dInner;
    hits += d;
  }

  float fresnel = 1.0 - max(0.0, dot(viewDirection, normal));
  fresnel = pow(fresnel, u_FresnelFalloff);
  fresnel = smoothstep(0.45, 0.65, fresnel);
  fresnel *= u_FresnelStrength;

  vec4 color = mix(u_HitPointColorA, u_HitPointColorB, hits*smoothstep(0.3, 0.6, noise.r));

  #if !defined(FLIP_SIDED)
    color.rgb += fresnel*u_FresnelColor;
  #endif

  gl_FragColor = color;
}`;

export function createNoiseTexture(size = 64): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    const v = Math.floor(Math.random() * 256);
    data[i * 4 + 0] = v;
    data[i * 4 + 1] = v;
    data[i * 4 + 2] = v;
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.needsUpdate = true;
  return tex;
}

export function createShieldMaterial(): THREE.ShaderMaterial {
  const mat = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    transparent: true,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: true,
    clipping: true,
    clipIntersection: true,
    defines: {
      HIT_POINTS_NUM: HIT_POINTS_NUM,
    },
    uniforms: {
      t_Noise: { value: createNoiseTexture(64) },
      u_Time: { value: 0 },
      u_HitPoints: { value: new Array(HIT_POINTS_NUM).fill(0).map(() => ({ position: new THREE.Vector3(999, 999, 999), size: 0, thickness: 0 })) },
      u_HitPointColorA: { value: new THREE.Vector4(0, 168/255, 245/255, 0.04) },
      u_HitPointColorB: { value: new THREE.Vector4(53/255, 175/255, 90/255, 0.8) },
      u_FresnelFalloff: { value: 0.75 },
      u_FresnelStrength: { value: 0.85 },
      u_FresnelColor: { value: new THREE.Color(1, 144/255, 0) },
    }
  });
  // Try to load the demo's noise texture for exact look, fallback stays
  try {
    const loader = new THREE.TextureLoader();
    loader.load(
      '/assets/shield/perlin-noise.png',
      (tex) => {
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        (mat.uniforms as any).t_Noise.value = tex;
      }
    );
  } catch {}
  return mat;
}
